/**
 * WhatWeOfferPage
 * Tells travellers what Wildy is today: a map of small wildlife and outdoor operators.
 * You find an operator, see their real photos, tours, prices and seasons, and book on their own site.
 * Wildy does not take bookings or payments at this stage; keep the copy in step with that.
 * Operators get one pointer to /for-business, the sister page built in the same style.
 */

import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ExternalLink, Map as MapIcon, Store, Search, Sprout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SectionHeader } from '@/components/ui/section-header';
import { Header } from '@/components/Header';
import { HeroSection } from '@/components/homepage/HeroSection';
import { Footer } from '@/components/homepage/Footer';
import { ExperienceBoxes } from '@/components/what-we-offer/ExperienceBoxes';
import { useCatalogueStats } from '@/components/what-we-offer/useCatalogueStats';

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

// Same pattern as ForBusinessPage; candidate for a shared component (see design-system proposals).
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
};

export function WhatWeOfferPage() {
  const { t } = useTranslation('public', { keyPrefix: 'offerings' });
  const navigate = useNavigate();
  const stats = useCatalogueStats();

  const steps = [
    { key: 'find',    icon: Search },
    { key: 'compare', icon: Store },
    { key: 'book',    icon: ExternalLink },
  ] as const;

  const faqs = t('faq.items', { returnObjects: true });
  const faqList = Array.isArray(faqs) ? faqs as { q: string; a: string }[] : [];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero: what Wildy is, in one line, and the page's main action */}
      {/* No height class: VideoHero sets its own minimum height and grows with its content. */}
      <HeroSection
        hideSearch
        hideFilters
        videoSrc="/video/hero-section-aurora-video-background.mp4"
        customContent={
          // Clears the fixed, translucent header: VideoHero already pads phones by max(5.5rem, safe-area + 4.5rem);
          // from sm up it pads only 2rem, so add 4rem + safe area here. Phone content is kept short so the hero stays compact.
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 sm:pt-[calc(4rem+env(safe-area-inset-top,0px))] text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm mb-6">
                <Sprout className="w-4 h-4" aria-hidden />
                {t('hero.badge')}
              </span>
              <h1
                className="text-[2rem] sm:text-6xl font-bold text-white tracking-tight leading-[1.1] sm:leading-[1.05]"
                style={{ textShadow: '0 4px 20px rgba(0,0,0,0.45)' }}
              >
                <span className="block">{t('hero.title.part1')}</span>
                <span className="block mt-2 bg-gradient-to-r from-action-200 to-action-300 bg-clip-text text-transparent">
                  {t('hero.title.part2')}
                </span>
              </h1>
              <p
                className="mt-4 sm:mt-6 mx-auto max-w-2xl text-base sm:text-xl text-white/95 leading-relaxed"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                {t('hero.description')}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" onClick={() => navigate('/')} className="group">
                  <MapIcon className="w-4 h-4 mr-2" aria-hidden />
                  {t('hero.cta.primary')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollTo('how-it-works')}
                  className="hidden sm:inline-flex border-white/50 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
                >
                  {t('hero.cta.secondary')}
                </Button>
              </div>
            </motion.div>
          </div>
        }
      />

      {/* The two kinds of experience: wildlife and adventure (keep first after the hero) */}
      <section id="experiences" className="py-20 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow={t('boxes.eyebrow')}
            title={t('boxes.title')}
            description={stats
              ? t('boxes.descriptionLive', { operators: stats.operators, countries: stats.countries })
              : t('boxes.description')}
          />
          <ExperienceBoxes stats={stats} />
        </div>
      </section>

      {/* How it works today */}
      <section id="how-it-works" className="py-20 sm:py-24 bg-brand-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow={t('how.eyebrow')} title={t('how.title')} />
          <ol className="grid gap-6 md:grid-cols-3">
            {steps.map(({ key, icon: Icon }, i) => (
              <motion.li key={key} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-primary font-bold">
                    {i + 1}
                  </span>
                  <Icon className="w-5 h-5 text-action-500" aria-hidden />
                </div>
                <h3 className="text-lg font-semibold text-brand-900 mb-2">{t(`how.steps.${key}.title`)}</h3>
                <p className="text-slate-600 leading-relaxed">{t(`how.steps.${key}.description`)}</p>
              </motion.li>
            ))}
          </ol>
          <p className="mt-8 text-center text-slate-600">{t('how.note')}</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow={t('faq.eyebrow')} title={t('faq.title')} />
          <Accordion type="single" collapsible className="rounded-lg border border-slate-200 bg-white px-6 shadow-sm">
            {faqList.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} className={i === faqList.length - 1 ? 'border-b-0' : ''}>
                <AccordionTrigger className="text-left text-base font-semibold text-brand-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing: explore, plus the one pointer for operators */}
      <section className="py-20 sm:py-24 bg-brand-50">
        <motion.div {...fadeUp} className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-900 tracking-tight [text-wrap:balance]">{t('closing.title')}</h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{t('closing.description')}</p>
          <Button size="lg" onClick={() => navigate('/')} className="group mt-8">
            {t('closing.cta')}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden />
          </Button>
          <div className="mt-12 rounded-lg border border-brand-200 bg-white p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-primary">
              <Store className="w-5 h-5" aria-hidden />
            </span>
            <p className="flex-1 text-slate-700 leading-relaxed">
              <span className="font-semibold text-brand-900">{t('operators.title')}</span>{' '}
              {t('operators.description')}
            </p>
            <Link
              to="/for-business"
              className="inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-4 shrink-0"
            >
              {t('operators.link')}
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
